import React from "react";
import { useQuery } from "react-query";
import { PageWrapper, Spinner } from "@cinesys/ui/components";
import InterfaceAccessNotice from "../../components/InterfaceAccessNotice";
import CustomerApiService, { ApiError } from "../../CustomerApiService";
import {
  GearMenuHost,
  createGearMenuRef,
  CollectionBrowser,
  AddCollectionModal,
  AddCollectionModalRef,
  RenameCollectionModal,
  RenameCollectionModalRef,
  RemoveCollectionModal,
  RemoveCollectionModalRef,
  AddItemsModal,
  AddItemsModalRef,
  getModal,
} from "@cinesys/experience-ui/components";

/**
 * Mounts the ported `cs_collection_browser` tree, which lives in `@cinesys/experience-ui`
 * (`cinesys-ui`'s `packages/experience-ui/src/components/CollectionBrowser`) alongside the shared gear menu.
 * Replaces `../collections-browser` entirely: that module was a from-scratch
 * breadcrumb-and-pagination screen with its own invented `CollectionResultGrid.tsx` Cantemo
 * doesn't have — condemned by `docs/implementation-plan.md` §0's verbatim-port rule.
 *
 * Cantemo's own `cs_collection_browser/index.tsx` mounts one `CollectionBrowser` instance plus
 * four modal singletons into `getModal()` roots, keyed on `window.cs_react_modals`. This screen
 * is that mounting code's equivalent — `CollectionBrowser.tsx`/`CollectionBrowserTree.tsx`'s own
 * unmodified code reads those same global refs directly (the "Add Collection" button, the gear
 * menu's Rename/Add Items/Delete entries — see `CollectionBrowserTree.tsx`'s own `Gear` usage),
 * so the modals have to be mounted somewhere real, not left as unreachable stand-ins.
 */
const menuRef = createGearMenuRef();

const modalRefs = {
  add: getModal<AddCollectionModalRef>("cs_collection_browser_add_collection"),
  rename: getModal<RenameCollectionModalRef>(
    "cs_collection_browser_rename_collection"
  ),
  remove: getModal<RemoveCollectionModalRef>(
    "cs_collection_browser_remove_collection"
  ),
  addItems: getModal<AddItemsModalRef>("cs_collection_browser_add_items"),
};

/**
 * `cs_collection_browser`'s backend (`apps/customer-api/src/routes/cs_collection_browser/
 * routes.ts`) has no dedicated "is this enabled" endpoint the way `home-dashboard`/`item-browser`
 * do (each of those returns `enabled` inline with its real payload) — every route here just 403s
 * when the `cs_collection_browser.enabled` flag is off. This probes the real listing endpoint
 * with `perPage: 1` purely to learn that verdict before rendering the tree; `FeatureGate` itself
 * isn't reused because it maps a 403 to the SAME "unauthenticated" notice a real 401 gets, which
 * would tell an admin whose org simply doesn't have this flag on yet that their session expired.
 */
function useCollectionBrowserEnabled() {
  return useQuery<boolean, ApiError>(
    ["cs_collection_browser", "enabled_probe"],
    () =>
      CustomerApiService.fetch("POST", "/cs_collection_browser/collections", {
        page: 0,
        perPage: 1,
        sort: [{ field: "title", order: "ascending" }],
      }).then(() => true),
    {
      staleTime: 30_000,
      retry: (failureCount, error) => error.status !== 403 && failureCount < 3,
    }
  );
}

const CollectionsBrowserScreen = () => {
  const enabledQuery = useCollectionBrowserEnabled();

  if (enabledQuery.isLoading) {
    return (
      <PageWrapper>
        <Spinner />
      </PageWrapper>
    );
  }

  if (enabledQuery.isError) {
    const reason =
      enabledQuery.error instanceof ApiError &&
      enabledQuery.error.status === 403
        ? "not-enabled"
        : enabledQuery.error instanceof ApiError &&
          enabledQuery.error.status === 401
        ? "unauthenticated"
        : "error";
    return <InterfaceAccessNotice reason={reason} />;
  }

  return (
    <PageWrapper>
      <CollectionBrowser fullPage />
      <GearMenuHost ref={menuRef.modalRef} />
      <AddCollectionModal ref={modalRefs.add.modalRef} />
      <RenameCollectionModal ref={modalRefs.rename.modalRef} />
      <RemoveCollectionModal ref={modalRefs.remove.modalRef} />
      <AddItemsModal ref={modalRefs.addItems.modalRef} />
    </PageWrapper>
  );
};

export default CollectionsBrowserScreen;
